// Timed don/doff statuses live on the ACTOR; status_origin_id identifies the inventory
// instance. Inventory itself does not move until completion, so status-only pushes must
// repaint every copy of its thumbnail, including a corpse's gear being doffed by a looter.
type Row = Record<string, unknown>;
type Activity = 'donning' | 'doffing';
const KINDS: Record<string, Activity> = {
    'D343892B-338C-4FBE-BEA0-2015FE503F43': 'donning',
    '398DD6E8-AFAE-EA0D-6FE9-C22842D53775': 'doffing',
};
const row = (v: unknown): Row => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : {};
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const id = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();

export function inventoryActivities(actor: unknown): Map<string, Activity> {
    const result = new Map<string, Activity>();
    const statuses = row(row(actor).statuses).target;
    if (!Array.isArray(statuses)) return result;
    for (const entry of statuses) {
        const st = row(entry);
        const kind = KINDS[id(st.status_item_id) || id(row(row(st.status_arr).items).item_id)];
        const origin = id(st.status_origin_id);
        if (!kind || !origin || Number(scalar(st.status_deleted) ?? 0) !== 0) continue;
        // Paused campaign time still represents an unfinished activity; keep its marker.
        result.set(origin, kind);
    }
    return result;
}

function actorId(actor: unknown): string {
    const a = row(actor);
    return id(row(a.encounter_creatures).encounter_creature_id)
        || id(row(a.player_characters).player_character_id) || id(row(a.items).item_id);
}

// Event snapshots supersede older duplicate rows in other caches. Store only the tiny
// activity projection, and replace it even when empty (cancel/finish must clear markers).
const applied = new Map<string, Map<string, Activity>>();
export function rememberInventoryActivities(actor: unknown, fallbackId = ''): void {
    const key = actorId(actor) || id(fallbackId);
    if (key) applied.set(key, inventoryActivities(actor));
}

function currentActivities(): Map<string, Activity> {
    const actors = new Map<string, Map<string, Activity>>();
    const ft = (window as unknown as {
        FateTide?: { encounterCache?: Map<string, unknown>; casterCache?: Map<string, unknown> };
    }).FateTide;
    const readActor = (a: unknown, fallback = '') => {
        const key = actorId(a) || id(fallback);
        if (key) actors.set(key, inventoryActivities(a));
    };
    ft?.encounterCache?.forEach(enc => {
        const roster = row(enc).encounter_creatures_arr;
        if (Array.isArray(roster)) roster.forEach(a => readActor(a));
    });
    ft?.casterCache?.forEach((a, key) => readActor(a, String(key)));
    applied.forEach((activities, key) => actors.set(key, activities));
    const result = new Map<string, Activity>();
    actors.forEach(activities => activities.forEach((kind, origin) => result.set(origin, kind)));
    return result;
}

function overlayHtml(kind: Activity): string {
    const label = kind === 'donning' ? 'Donning' : 'Doffing';
    return `<span class="ft-inventory-activity" data-ft-inventory-activity="${kind}" role="img" aria-label="${label} in progress">`
        + `<span aria-hidden="true">${iconMap(kind, 32, 32)}</span></span>`;
}

export function syncInventoryActivities(): void {
    const activities = currentActivities();
    document.querySelectorAll<HTMLElement>('.inventory-item[data-inventory_id]').forEach(tile => {
        const kind = activities.get(id(tile.dataset.inventory_id));
        const host = tile.querySelector<HTMLElement>(':scope > .position-relative');
        if (!host) return;
        const existing = host.querySelector<HTMLElement>(':scope > .ft-inventory-activity');
        if (existing?.dataset.ftInventoryActivity === kind) return;
        existing?.remove();
        if (kind) host.insertAdjacentHTML('beforeend', overlayHtml(kind));
    });
}

let scheduled = false;
// Renderers return HTML before insertion. One frame later covers newly opened tabs,
// container panels and PHP mounts, without polling or a page-wide mutation observer.
export function queueInventoryActivities(): void {
    if (scheduled || typeof window.requestAnimationFrame !== 'function') return;
    scheduled = true;
    window.requestAnimationFrame(() => {
        scheduled = false;
        syncInventoryActivities();
    });
}

export function installInventoryActivityListeners(): void {
    window.addEventListener('ft:caster-state-applied', event => {
        const detail = row((event as CustomEvent).detail);
        rememberInventoryActivities(detail.casterArr, String(detail.casterId ?? ''));
        queueInventoryActivities();
    });
    const encounterChanged = (event: Event) => {
        const detail = row((event as CustomEvent).detail);
        const roster = row(detail.encounterArr).encounter_creatures_arr;
        // For row deltas, only the changed actors are authoritative; untouched roster
        // entries can predate a more recent caster_update.
        const changed = Array.isArray(detail.changedCreatureIds)
            ? new Set(detail.changedCreatureIds.map(id)) : null;
        if (Array.isArray(roster)) roster.forEach(actor => {
            if (!changed || changed.has(actorId(actor))) rememberInventoryActivities(actor);
        });
        queueInventoryActivities();
    };
    window.addEventListener('ft:encounter-installed', encounterChanged);
    window.addEventListener('ft:encounter-state-received', encounterChanged);
}
